import { useState } from "react";
import styled from "styled-components";

export interface FooterProps {
  registerText: string;
  aboutText: string;
  helpText: string;
  helpSteps: string[];
  isDesktop: boolean;
  registerUrl: string;
  aboutUrl: string;
}

const MobileFooter = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px 32px;
  color: #23231f;

  @media screen and (min-width: 481px) {
    display: none;
  }
`;

const DesktopFooter = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 24px 16px 32px;
  color: #23231f;

  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const DesktopHelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 2rem;
  color: #23231f;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #55544c;
  }
`;

const LightbulbIcon = styled.span`
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
`;

const FooterLink = styled.a`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 1.8rem;
  font-weight: 500;
  line-height: 2.2rem;
  color: #23231f;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 16px 12px 16px 16px;
  height: 56px;
  cursor: pointer;
  width: fit-content;
  text-decoration: none;

  @media (hover: hover) {
    &:hover {
      background: #163563;
      color: #f7f7f6;
      text-decoration: none;
    }
  }

  &:focus-visible {
    background: #163563;
    color: #f7f7f6;
    box-shadow:
      0px 0px 0px 2px #f7f7f6,
      0px 0px 0px 4px #126de3;
    outline: none;
  }
`;

const Icon = styled.span`
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
`;

const HelpOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 20;
`;

const HelpDialog = styled.div`
  width: min(560px, 100%);
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  color: #23231f;
  box-shadow: 0px 12px 30px rgba(0, 0, 0, 0.2);
`;

const HelpHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const HelpTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.headingFontFamily};
  font-size: 2rem;
  font-weight: 500;
  color: #23231f;
`;

const HelpCloseButton = styled.button`
  border: none;
  background: transparent;
  color: #23231f;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #55544c;
  }
`;

const HelpList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HelpListItem = styled.li`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 12px;
  align-items: start;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 1.6rem;
  line-height: 2rem;
  color: #23231f;
`;

const HelpStepNumber = styled.span`
  font-weight: 500;
  color: #6f6e67;
`;

const HelpStepText = styled.span``;

export default function Footer(props: FooterProps): JSX.Element | null {
  const [isHelpOpen, setHelpOpen] = useState(false);
  const helpSteps = props.helpSteps.filter((step) => step.trim().length > 0);

  return (
    <>
      <DesktopFooter data-testid="footer_desktop">
        <DesktopHelpButton
          type="button"
          data-testid="footer_help_desktop"
          onClick={() => setHelpOpen(true)}
        >
          {props.helpText}
          <LightbulbIcon aria-hidden="true">
            <svg
              width="12"
              height="17"
              viewBox="0 0 12 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.33333 15.8333C3.33333 16.2917 3.70833 16.6667 4.16667 16.6667H7.5C7.95833 16.6667 8.33333 16.2917 8.33333 15.8333V15H3.33333V15.8333ZM5.83333 0C2.61667 0 0 2.61667 0 5.83333C0 7.81667 0.991667 9.55833 2.5 10.6167V12.5C2.5 12.9583 2.875 13.3333 3.33333 13.3333H8.33333C8.79167 13.3333 9.16667 12.9583 9.16667 12.5V10.6167C10.675 9.55833 11.6667 7.81667 11.6667 5.83333C11.6667 2.61667 9.05 0 5.83333 0ZM8.20833 9.25L7.5 9.75V11.6667H4.16667V9.75L3.45833 9.25C2.33333 8.46667 1.66667 7.19167 1.66667 5.83333C1.66667 3.53333 3.53333 1.66667 5.83333 1.66667C8.13333 1.66667 10 3.53333 10 5.83333C10 7.19167 9.33333 8.46667 8.20833 9.25Z"
                fill="currentColor"
              />
            </svg>
          </LightbulbIcon>
        </DesktopHelpButton>
      </DesktopFooter>
      <MobileFooter data-testid="footer_mobile">
        <FooterLink
          href={props.registerUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer_register"
        >
          {props.registerText}
          <Icon aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM5.35 16.5C6.66 15.56 8.26 15 10 15C11.74 15 13.34 15.56 14.65 16.5C13.34 17.44 11.74 18 10 18C8.26 18 6.66 17.44 5.35 16.5ZM16.14 15.12C14.45 13.8 12.32 13 10 13C7.68 13 5.55 13.8 3.86 15.12C2.7 13.73 2 11.95 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 11.95 17.3 13.73 16.14 15.12Z"
                fill="currentColor"
              />
              <path
                d="M10 4C8.07 4 6.5 5.57 6.5 7.5C6.5 9.43 8.07 11 10 11C11.93 11 13.5 9.43 13.5 7.5C13.5 5.57 11.93 4 10 4ZM10 9C9.17 9 8.5 8.33 8.5 7.5C8.5 6.67 9.17 6 10 6C10.83 6 11.5 6.67 11.5 7.5C11.5 8.33 10.83 9 10 9Z"
                fill="currentColor"
              />
            </svg>
          </Icon>
        </FooterLink>
        <FooterLink
          href={props.aboutUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer_about"
        >
          {props.aboutText}
          <Icon aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 -960 960 960"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
            </svg>
          </Icon>
        </FooterLink>
      </MobileFooter>
      {isHelpOpen && (
        <HelpOverlay
          data-testid="help_modal"
          onClick={() => setHelpOpen(false)}
        >
          <HelpDialog
            role="dialog"
            aria-modal="true"
            aria-label={props.helpText}
            onClick={(event) => event.stopPropagation()}
          >
            <HelpHeader>
              <HelpTitle>{props.helpText}</HelpTitle>
              <HelpCloseButton
                type="button"
                onClick={() => setHelpOpen(false)}
                aria-label="Close"
                data-testid="help_modal_close"
              >
                ×
              </HelpCloseButton>
            </HelpHeader>
            <HelpList>
              {helpSteps.map((step, index) => (
                <HelpListItem key={step}>
                  <HelpStepNumber>{index + 1}</HelpStepNumber>
                  <HelpStepText>{step}</HelpStepText>
                </HelpListItem>
              ))}
            </HelpList>
          </HelpDialog>
        </HelpOverlay>
      )}
    </>
  );
}
